import React, { useState, useRef, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { initialTiles } from '../utils/mockData';
import { loadPayoutRequests, updatePayoutStatus } from '../utils/affiliate';
import { loadAllUsers } from '../utils/usersDb';
import * as FiIcons from 'react-icons/fi';
const {
  FiX, FiShield, FiTrash2, FiEye, FiEyeOff, FiGrid, FiUsers, FiDollarSign,
  FiActivity, FiAlertTriangle, FiRefreshCw, FiDownload, FiEdit2, FiCheck,
  FiMapPin, FiUpload, FiLink, FiArrowLeft, FiTrendingUp, FiZap, FiSend,
  FiClock, FiXCircle, FiMail, FiUser, FiShoppingBag,
} = FiIcons;

const ADMIN_PASSWORD = 'Supportme9(';
const MOCK_IDS = new Set(initialTiles.map(t => t.id));
const STORAGE_KEY = 'admin_authed_v1';

// ── Source badge ──────────────────────────────────────────────────────────────
function SourceBadge({ source, referralCode }) {
  if (source === 'referral' && referralCode) {
    return (
      <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded font-black uppercase shrink-0" style={{ background: 'rgba(0,255,136,0.12)', color: '#00ff88', border: '1px solid rgba(0,255,136,0.3)' }}>
        <FiTrendingUp className="text-[8px]" /> ref:{referralCode}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded font-black uppercase shrink-0" style={{ background: 'rgba(0,200,255,0.1)', color: '#00ccff', border: '1px solid rgba(0,200,255,0.25)' }}>
      <FiZap className="text-[8px]" /> direct
    </span>
  );
}

// ── Attribution summary ───────────────────────────────────────────────────────
function AttributionSummary({ tiles }) {
  const real = tiles.filter(t => !MOCK_IDS.has(t.id));
  const referred = real.filter(t => t.source === 'referral');
  const direct = real.filter(t => t.source !== 'referral');
  const byCode = {};
  referred.forEach(t => {
    const code = t.referralCode || 'unknown';
    if (!byCode[code]) byCode[code] = { code, count: 0, pixels: 0 };
    byCode[code].count += 1;
    byCode[code].pixels += t.w * t.h;
  });
  const topCodes = Object.values(byCode).sort((a, b) => b.pixels - a.pixels).slice(0, 5);
  const referredPct = real.length > 0 ? Math.round((referred.length / real.length) * 100) : 0;

  return (
    <div className="px-6 py-4 border-b border-pink-500/10 space-y-3 shrink-0">
      <div className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-1">
        <FiTrendingUp className="text-pink-500" /> Sale Attribution
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl p-3 text-center" style={{ background: 'rgba(0,200,255,0.06)', border: '1px solid rgba(0,200,255,0.2)' }}>
          <div className="font-black text-lg" style={{ color: '#00ccff' }}>{direct.length}</div>
          <div className="text-gray-500 text-[10px] uppercase tracking-wider">Direct</div>
        </div>
        <div className="rounded-xl p-3 text-center" style={{ background: 'rgba(0,255,136,0.06)', border: '1px solid rgba(0,255,136,0.2)' }}>
          <div className="font-black text-lg" style={{ color: '#00ff88' }}>{referred.length}</div>
          <div className="text-gray-500 text-[10px] uppercase tracking-wider">Referred</div>
        </div>
        <div className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,0,200,0.06)', border: '1px solid rgba(255,0,200,0.2)' }}>
          <div className="font-black text-lg" style={{ color: '#ff00cc' }}>{referredPct}%</div>
          <div className="text-gray-500 text-[10px] uppercase tracking-wider">Ref Rate</div>
        </div>
      </div>
      {topCodes.length > 0 && (
        <div>
          <div className="text-[10px] text-gray-600 uppercase tracking-widest mb-1.5">Top Referral Codes</div>
          <div className="space-y-1">
            {topCodes.map(c => (
              <div key={c.code} className="flex items-center justify-between px-3 py-1.5 rounded-lg" style={{ background: 'rgba(0,255,136,0.04)', border: '1px solid rgba(0,255,136,0.1)' }}>
                <span className="font-mono text-xs font-black" style={{ color: '#00ff88' }}>{c.code}</span>
                <div className="flex items-center gap-3 text-[11px] text-gray-500">
                  <span>{c.count} sale{c.count !== 1 ? 's' : ''}</span>
                  <span className="font-black text-white">{c.pixels.toLocaleString()} px</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Payouts tab ───────────────────────────────────────────────────────────────
function PayoutsTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    loadPayoutRequests().then(data => { setRequests(data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleStatus = async (id, status) => {
    setUpdating(id);
    try { await updatePayoutStatus(id, status); setRequests(prev => prev.map(r => r.id === id ? { ...r, status } : r)); } catch {}
    setUpdating(null);
  };

  const statusColor = { pending: '#f59e0b', approved: '#00ff88', paid: '#00ccff', rejected: '#ff4444' };
  const statusIcon = { pending: FiClock, approved: FiCheck, paid: FiSend, rejected: FiXCircle };

  if (loading) return <div className="text-center py-10 text-gray-600">Loading payout requests...</div>;
  if (requests.length === 0) return (
    <div className="text-center py-16 text-gray-600">
      <FiDollarSign className="text-4xl mx-auto mb-3 opacity-30" />
      <p className="text-sm">No payout requests yet.</p>
    </div>
  );

  return (
    <div className="space-y-3">
      {requests.map(r => {
        const Icon = statusIcon[r.status] || FiClock;
        return (
          <div key={r.id} className="px-4 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${r.status === 'pending' ? 'rgba(245,158,11,0.3)' : 'rgba(255,255,255,0.07)'}` }}>
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-black text-white text-sm">{r.affiliate_email}</span>
                  <span className="font-mono text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(0,255,136,0.1)', color: '#00ff88' }}>{r.affiliate_code}</span>
                  <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-black uppercase" style={{ background: `${statusColor[r.status]}18`, color: statusColor[r.status], border: `1px solid ${statusColor[r.status]}44` }}>
                    <Icon className="text-[9px]" /> {r.status}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-gray-500 mb-2">
                  <div><span className="text-gray-600">Amount:</span> <span className="font-black text-white">${r.amount}</span></div>
                  <div><span className="text-gray-600">Method:</span> <span className="text-white capitalize">{r.method}</span></div>
                  <div className="col-span-2"><span className="text-gray-600">Address:</span> <span className="font-mono text-gray-300 break-all">{r.address}</span></div>
                </div>
                {r.notes && <div className="text-xs text-gray-500 mb-2"><span className="text-gray-600">Notes:</span> {r.notes}</div>}
                <div className="text-[10px] text-gray-700">{new Date(r.created_at).toLocaleString()}</div>
              </div>
              {r.status === 'pending' && (
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button onClick={() => handleStatus(r.id, 'paid')} disabled={updating === r.id} className="px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-wider disabled:opacity-60" style={{ background: 'rgba(0,255,136,0.15)', color: '#00ff88', border: '1px solid rgba(0,255,136,0.35)' }}>✓ Mark Paid</button>
                  <button onClick={() => handleStatus(r.id, 'rejected')} disabled={updating === r.id} className="px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-wider disabled:opacity-60" style={{ background: 'rgba(255,50,50,0.1)', color: '#ff4444', border: '1px solid rgba(255,50,50,0.3)' }}>✕ Reject</button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Users tab ─────────────────────────────────────────────────────────────────
function UsersTab({ tiles }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadAllUsers()
      .then(data => { setUsers(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Build set of buyer emails from real tiles
  const buyerEmails = new Set(
    tiles.filter(t => !MOCK_IDS.has(t.id) && t.ownerEmail).map(t => t.ownerEmail.toLowerCase())
  );

  const enriched = users.map(u => ({
    ...u,
    hasBought: buyerEmails.has((u.email || '').toLowerCase()),
  }));

  const filtered = enriched.filter(u => {
    const matchSearch = (u.email || '').toLowerCase().includes(search.toLowerCase()) ||
      (u.name || '').toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || (filter === 'buyers' && u.hasBought) || (filter === 'leads' && !u.hasBought);
    return matchSearch && matchFilter;
  });

  const buyerCount = enriched.filter(u => u.hasBought).length;
  const leadCount = enriched.filter(u => !u.hasBought).length;

  const exportCSV = () => {
    const rows = [['Name', 'Email', 'Status', 'Signed Up']];
    filtered.forEach(u => rows.push([
      u.name || '',
      u.email || '',
      u.hasBought ? 'Buyer' : 'Lead',
      new Date(u.created_at).toLocaleDateString(),
    ]));
    const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'users_export.csv'; a.click();
  };

  if (loading) return <div className="text-center py-10 text-gray-600">Loading users...</div>;

  return (
    <div className="space-y-3">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,0,200,0.05)', border: '1px solid rgba(255,0,200,0.2)' }}>
          <div className="font-black text-lg text-white">{enriched.length}</div>
          <div className="text-gray-500 text-[10px] uppercase tracking-wider">Total Users</div>
        </div>
        <div className="rounded-xl p-3 text-center" style={{ background: 'rgba(0,255,136,0.05)', border: '1px solid rgba(0,255,136,0.2)' }}>
          <div className="font-black text-lg" style={{ color: '#00ff88' }}>{buyerCount}</div>
          <div className="text-gray-500 text-[10px] uppercase tracking-wider">Buyers</div>
        </div>
        <div className="rounded-xl p-3 text-center" style={{ background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.2)' }}>
          <div className="font-black text-lg" style={{ color: '#f59e0b' }}>{leadCount}</div>
          <div className="text-gray-500 text-[10px] uppercase tracking-wider">Leads (no purchase)</div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search name or email..."
          className="flex-1 min-w-[160px] px-4 py-2 rounded-xl text-white text-sm focus:outline-none"
          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,0,200,0.2)' }}
        />
        <div className="flex rounded-xl overflow-hidden border border-gray-800 text-[11px]">
          {[
            { id: 'all', label: `All (${enriched.length})` },
            { id: 'buyers', label: `Buyers (${buyerCount})` },
            { id: 'leads', label: `Leads (${leadCount})` },
          ].map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              className="px-3 py-2 font-black uppercase tracking-wider transition-all"
              style={filter === f.id ? { background: 'rgba(255,0,200,0.15)', color: '#ff00cc' } : { color: '#6b7280' }}>
              {f.label}
            </button>
          ))}
        </div>
        <button onClick={exportCSV} className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider hover:scale-105" style={{ background: 'rgba(0,255,136,0.15)', border: '1px solid rgba(0,255,136,0.3)', color: '#00ff88' }}>
          <FiDownload /> CSV
        </button>
      </div>

      {/* User list */}
      {filtered.length === 0 && <div className="text-center text-gray-600 py-10">No users found.</div>}
      <div className="space-y-2">
        {filtered.map(u => (
          <div key={u.id} className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${u.hasBought ? 'rgba(0,255,136,0.15)' : 'rgba(245,158,11,0.15)'}` }}>
            {/* Avatar */}
            <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 font-black text-sm" style={{ background: u.hasBought ? 'rgba(0,255,136,0.12)' : 'rgba(245,158,11,0.12)', color: u.hasBought ? '#00ff88' : '#f59e0b' }}>
              {(u.name || u.email || '?')[0].toUpperCase()}
            </div>
            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-black text-white text-sm truncate">{u.name || <span className="text-gray-600 font-normal italic">No name</span>}</span>
                {u.hasBought ? (
                  <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded font-black uppercase shrink-0" style={{ background: 'rgba(0,255,136,0.12)', color: '#00ff88', border: '1px solid rgba(0,255,136,0.3)' }}>
                    <FiShoppingBag className="text-[8px]" /> Buyer
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded font-black uppercase shrink-0" style={{ background: 'rgba(245,158,11,0.12)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)' }}>
                    <FiMail className="text-[8px]" /> Lead
                  </span>
                )}
              </div>
              <div className="text-gray-500 text-xs mt-0.5 flex items-center gap-1">
                <FiMail className="text-[10px]" /> {u.email}
              </div>
            </div>
            {/* Date */}
            <div className="text-[10px] text-gray-700 shrink-0 text-right">
              {new Date(u.created_at).toLocaleDateString()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Image swap / edit helpers ─────────────────────────────────────────────────
function ImageSwapRow({ tile, onSave }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('upload');
  const [urlVal, setUrlVal] = useState(tile.image || '');
  const [preview, setPreview] = useState('');
  const inputRef = useRef(null);
  const handleFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
  };
  const handleSave = () => { const val = mode === 'upload' ? preview : urlVal; if (val) { onSave(val); setOpen(false); setPreview(''); } };
  if (!open) return (
    <button onClick={() => setOpen(true)} className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-pink-400 transition-colors mt-0.5">
      <FiUpload className="text-[10px]" /> Swap image
    </button>
  );
  return (
    <div className="mt-2 p-2 rounded-lg space-y-2" style={{ background: 'rgba(255,0,200,0.05)', border: '1px solid rgba(255,0,200,0.2)' }}>
      <div className="flex rounded-lg overflow-hidden border border-gray-800 text-[10px]">
        <button type="button" onClick={() => setMode('upload')} className="flex-1 py-1.5 font-bold transition-all flex items-center justify-center gap-1" style={mode === 'upload' ? { background: 'rgba(255,0,200,0.15)', color: '#ff00cc' } : { color: '#6b7280' }}><FiUpload className="text-[10px]" /> Upload</button>
        <button type="button" onClick={() => setMode('url')} className="flex-1 py-1.5 font-bold transition-all flex items-center justify-center gap-1" style={mode === 'url' ? { background: 'rgba(255,0,200,0.15)', color: '#ff00cc' } : { color: '#6b7280' }}><FiLink className="text-[10px]" /> URL</button>
      </div>
      {mode === 'upload' ? (
        <div onClick={() => inputRef.current?.click()} className="w-full h-16 rounded-lg flex flex-col items-center justify-center gap-1 cursor-pointer" style={{ background: 'rgba(255,255,255,0.03)', border: '1.5px dashed rgba(255,0,200,0.3)' }} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}>
          {preview ? <img src={preview} alt="preview" className="h-full w-full object-contain rounded-lg p-0.5" /> : <><FiUpload className="text-pink-500 text-sm" /><span className="text-[10px] text-gray-500">Drop or <span style={{ color: '#ff00cc' }}>browse</span></span></>}
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={e => handleFile(e.target.files?.[0])} />
        </div>
      ) : (
        <input type="url" value={urlVal} onChange={e => setUrlVal(e.target.value)} placeholder="https://..." className="w-full px-2 py-1.5 rounded-lg text-white text-xs focus:outline-none" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,0,200,0.3)' }} />
      )}
      <div className="flex items-center gap-2">
        <button onClick={handleSave} disabled={mode === 'upload' ? !preview : !urlVal} className="flex-1 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider text-white disabled:opacity-40" style={{ background: 'linear-gradient(135deg,#ff00cc,#cc00ff)' }}>Save</button>
        <button onClick={() => { setOpen(false); setPreview(''); }} className="px-3 py-1 rounded-lg text-[10px] text-gray-400 hover:text-white" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>Cancel</button>
      </div>
    </div>
  );
}

function EditRow({ label, value, onSave, type = 'text' }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');
  const handleSave = () => { onSave(val); setEditing(false); };
  if (!editing) return (
    <button onClick={() => setEditing(true)} className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-pink-400 transition-colors mt-0.5">
      <FiEdit2 className="text-[10px]" /> {label}
    </button>
  );
  return (
    <div className="flex items-center gap-2 mt-1">
      <input type={type} value={val} onChange={e => setVal(e.target.value)} placeholder={type === 'url' ? 'https://...' : '...'} className="flex-1 px-2 py-1 rounded-lg text-white text-xs focus:outline-none" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,0,200,0.4)' }} autoFocus />
      <button onClick={handleSave} className="w-7 h-7 rounded-lg flex items-center justify-center bg-green-500/20 hover:bg-green-500/40 text-green-400"><FiCheck className="text-xs" /></button>
      <button onClick={() => setEditing(false)} className="w-7 h-7 rounded-lg flex items-center justify-center bg-white/10 hover:bg-white/20 text-gray-400"><FiX className="text-xs" /></button>
    </div>
  );
}

// ── Main AdminPanel ───────────────────────────────────────────────────────────
export default function AdminPanel({ onClose, inline = false }) {
  const { tiles, updateTileField, removeTile, clearAllTiles, locateTileOnGrid } = useAppContext();
  const [authed, setAuthed] = useState(() => { try { return localStorage.getItem(STORAGE_KEY) === 'true'; } catch { return false; } });
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [tab, setTab] = useState('tiles');
  const [hiddenTiles, setHiddenTiles] = useState({});
  const [confirmClear, setConfirmClear] = useState(false);
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [saving, setSaving] = useState(false);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) { setAuthed(true); setAuthError(''); try { localStorage.setItem(STORAGE_KEY, 'true'); } catch {} }
    else setAuthError('Incorrect password.');
  };
  const handleLogout = () => { setAuthed(false); try { localStorage.removeItem(STORAGE_KEY); } catch {} };
  const toggleHide = (id) => setHiddenTiles(prev => ({ ...prev, [id]: !prev[id] }));
  const handleClearAll = async () => { setSaving(true); await clearAllTiles(); setSaving(false); setConfirmClear(false); };
  const handleImageSave = (id, val) => updateTileField(id, 'image', val);
  const handleLinkSave = (id, val) => updateTileField(id, 'link', val);
  const handleLocate = (tileId) => { onClose(); locateTileOnGrid(tileId); };

  const exportCSV = () => {
    const rows = [['ID', 'Title', 'Link', 'X', 'Y', 'W', 'H', 'Pixels', 'Clicks', 'Glow', 'Source', 'ReferralCode', 'Type']];
    tiles.forEach(t => rows.push([t.id, t.title, t.link, t.x, t.y, t.w, t.h, t.w * t.h, t.clicks, t.hasGlow, t.source || 'direct', t.referralCode || '', MOCK_IDS.has(t.id) ? 'MOCK' : 'REAL']));
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'pixels_export.csv'; a.click();
  };

  const totalRevenue = tiles.reduce((acc, t) => acc + (t.w * t.h * 0.25), 0);
  const mockCount = tiles.filter(t => MOCK_IDS.has(t.id)).length;
  const realCount = tiles.length - mockCount;

  const filteredTiles = tiles.filter(t => {
    const matchSearch = t.title?.toLowerCase().includes(search.toLowerCase()) || t.link?.toLowerCase().includes(search.toLowerCase());
    const isMock = MOCK_IDS.has(t.id);
    const matchFilter = filterMode === 'all' || (filterMode === 'mock' && isMock) || (filterMode === 'real' && !isMock);
    const matchSource = sourceFilter === 'all' || (sourceFilter === 'direct' && (t.source || 'direct') === 'direct') || (sourceFilter === 'referral' && t.source === 'referral');
    return matchSearch && matchFilter && matchSource;
  });

  if (!authed) {
    const loginCard = (
      <div className="relative w-full max-w-sm mx-auto">
        <div className="absolute -inset-[1.5px] rounded-2xl" style={{ background: 'linear-gradient(135deg,#ff00cc,#cc00ff)' }} />
        <div className="relative rounded-2xl p-8" style={{ background: '#080012' }}>
          {!inline && (<button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 text-gray-400 hover:text-white flex items-center justify-center transition-all"><FiX /></button>)}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#ff00cc,#cc00ff)' }}><FiShield className="text-white text-lg" /></div>
            <div>
              <h2 className="font-black text-white text-xl uppercase tracking-wider" style={{ fontFamily: 'Impact,sans-serif' }}>Admin Access</h2>
              <p className="text-gray-500 text-xs">1 Million Pixels — Restricted</p>
            </div>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-1.5">Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter admin password" className="w-full px-4 py-3 rounded-xl text-white text-sm focus:outline-none" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,0,200,0.3)' }} />
              {authError && <p className="text-red-400 text-xs mt-1">{authError}</p>}
            </div>
            <button type="submit" className="w-full py-3 rounded-xl font-black text-sm uppercase tracking-widest text-white transition-all hover:scale-[1.02]" style={{ background: 'linear-gradient(135deg,#ff00cc,#cc00ff)', fontFamily: 'Impact,sans-serif' }}>Enter Admin</button>
          </form>
        </div>
      </div>
    );
    if (inline) return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6" style={{ background: '#080012' }}>
        <div className="w-full max-w-sm">
          {loginCard}
          <div className="mt-6 text-center">
            <button onClick={onClose} className="flex items-center gap-2 text-gray-600 hover:text-pink-400 transition-colors text-xs uppercase tracking-widest mx-auto"><FiArrowLeft className="text-xs" /> Back to grid</button>
          </div>
        </div>
      </div>
    );
    return (<div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-md p-4">{loginCard}</div>);
  }

  const TABS = [
    { id: 'tiles', label: 'Manage Tiles', icon: FiGrid },
    { id: 'users', label: 'Users', icon: FiUsers },
    { id: 'payouts', label: 'Payouts', icon: FiDollarSign },
    { id: 'danger', label: 'Danger Zone', icon: FiAlertTriangle },
  ];

  const panelBody = (
    <>
      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 px-6 py-4 border-b border-pink-500/10 shrink-0">
        {[
          { icon: FiGrid, label: 'Total Tiles', value: tiles.length, color: '#ff00cc' },
          { icon: FiDollarSign, label: 'Est. Revenue', value: `$${totalRevenue.toFixed(0)}`, color: '#00ff88' },
          { icon: FiActivity, label: 'Real Buyers', value: realCount, color: '#00ffff' },
          { icon: FiUsers, label: 'Mock Tiles', value: mockCount, color: '#f59e0b' },
        ].map((s, i) => (
          <div key={i} className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <s.icon className="mx-auto mb-1 text-sm" style={{ color: s.color }} />
            <div className="font-black text-lg" style={{ color: s.color }}>{s.value}</div>
            <div className="text-gray-500 text-[10px] uppercase tracking-wider">{s.label}</div>
          </div>
        ))}
      </div>

      <AttributionSummary tiles={tiles} />

      {/* Tabs */}
      <div className="flex border-b border-pink-500/10 px-6 shrink-0 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-4 py-3 text-xs font-black uppercase tracking-wider border-b-2 transition-all whitespace-nowrap ${tab === t.id ? 'border-pink-500' : 'border-transparent text-gray-500 hover:text-gray-300'}`} style={tab === t.id ? { color: '#ff00cc' } : {}}>
            <t.icon className="text-sm" /> {t.label}
          </button>
        ))}
        <div className="flex-1" />
        <button onClick={handleLogout} className="flex items-center gap-1.5 px-3 py-2 my-1 rounded-lg text-xs text-gray-500 hover:text-red-400 transition-colors shrink-0" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>Sign Out</button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4">
        {tab === 'tiles' && (
          <div className="space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tiles..." className="flex-1 min-w-[160px] px-4 py-2 rounded-xl text-white text-sm focus:outline-none" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,0,200,0.2)' }} />
              <div className="flex rounded-xl overflow-hidden border border-gray-800 text-[11px]">
                {[{ id: 'all', label: `All (${tiles.length})` }, { id: 'mock', label: `Mock (${mockCount})` }, { id: 'real', label: `Real (${realCount})` }].map(f => (
                  <button key={f.id} onClick={() => setFilterMode(f.id)} className="px-3 py-2 font-black uppercase tracking-wider transition-all" style={filterMode === f.id ? { background: 'rgba(255,0,200,0.15)', color: '#ff00cc' } : { color: '#6b7280' }}>{f.label}</button>
                ))}
              </div>
              <div className="flex rounded-xl overflow-hidden border border-gray-800 text-[11px]">
                {[{ id: 'all', label: 'All Sources' }, { id: 'direct', label: 'Direct' }, { id: 'referral', label: 'Referred' }].map(f => (
                  <button key={f.id} onClick={() => setSourceFilter(f.id)} className="px-3 py-2 font-black uppercase tracking-wider transition-all" style={sourceFilter === f.id ? { background: 'rgba(0,255,136,0.15)', color: '#00ff88' } : { color: '#6b7280' }}>{f.label}</button>
                ))}
              </div>
              <button onClick={exportCSV} className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider hover:scale-105" style={{ background: 'rgba(0,255,136,0.15)', border: '1px solid rgba(0,255,136,0.3)', color: '#00ff88' }}>
                <FiDownload /> CSV
              </button>
            </div>
            <div className="space-y-2">
              {filteredTiles.length === 0 && <div className="text-center text-gray-600 py-10">No tiles found.</div>}
              {filteredTiles.map(tile => {
                const isMock = MOCK_IDS.has(tile.id);
                return (
                  <div key={tile.id} className={`flex items-start gap-3 px-4 py-3 rounded-xl transition-all ${hiddenTiles[tile.id] ? 'opacity-40' : ''}`} style={{ background: isMock ? 'rgba(245,158,11,0.04)' : 'rgba(255,255,255,0.03)', border: `1px solid ${isMock ? 'rgba(245,158,11,0.2)' : 'rgba(255,255,255,0.06)'}` }}>
                    <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0 border border-gray-700 flex items-center justify-center" style={{ background: tile.solidColor || '#1a0030' }}>
                      {tile.image ? <img src={tile.image} alt={tile.title} className="w-full h-full object-cover" /> : <div className="w-full h-full" style={{ background: tile.solidColor || '#1a0030' }} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-black text-white text-sm truncate">{tile.title}</span>
                        {isMock && <span className="text-[9px] px-1.5 py-0.5 rounded font-black uppercase shrink-0" style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)' }}>MOCK</span>}
                        {!isMock && <span className="text-[9px] px-1.5 py-0.5 rounded font-black uppercase shrink-0" style={{ background: 'rgba(0,255,136,0.1)', color: '#00ff88', border: '1px solid rgba(0,255,136,0.25)' }}>REAL</span>}
                        {tile.hasGlow && <span className="text-[9px] px-1.5 py-0.5 rounded font-bold shrink-0" style={{ background: 'rgba(255,0,200,0.15)', color: '#ff00cc' }}>GLOW</span>}
                        {!isMock && <SourceBadge source={tile.source || 'direct'} referralCode={tile.referralCode} />}
                      </div>
                      <div className="text-gray-500 text-xs truncate max-w-[220px]">{tile.link}</div>
                      <ImageSwapRow tile={tile} onSave={(val) => handleImageSave(tile.id, val)} />
                      <EditRow label="Change link URL" value={tile.link} type="url" onSave={(val) => handleLinkSave(tile.id, val)} />
                    </div>
                    <div className="hidden sm:flex flex-col items-end text-xs text-gray-500 shrink-0 mt-0.5">
                      <span className="font-mono">{tile.w}×{tile.h}px</span>
                      <span className="font-mono text-[10px]">x:{tile.x} y:{tile.y}</span>
                      <span>{(tile.w * tile.h).toLocaleString()} px</span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
                      <button onClick={() => handleLocate(tile.id)} title="Locate on grid" className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-yellow-500/20 text-gray-400 hover:text-yellow-400"><FiMapPin className="text-sm" /></button>
                      <button onClick={() => toggleHide(tile.id)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 text-gray-400 hover:text-white">{hiddenTiles[tile.id] ? <FiEye /> : <FiEyeOff />}</button>
                      <button onClick={() => removeTile(tile.id)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-red-500/20 text-gray-400 hover:text-red-400"><FiTrash2 /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === 'users' && <UsersTab tiles={tiles} />}
        {tab === 'payouts' && <PayoutsTab />}

        {tab === 'danger' && (
          <div className="space-y-4 max-w-lg">
            <div className="p-4 rounded-xl border border-red-500/30" style={{ background: 'rgba(255,50,50,0.05)' }}>
              <h3 className="font-black text-red-400 text-sm uppercase tracking-wider mb-1 flex items-center gap-2"><FiAlertTriangle /> Clear All Tiles</h3>
              <p className="text-gray-400 text-xs mb-3">Removes every tile from the grid and database. Cannot be undone.</p>
              {!confirmClear ? (
                <button onClick={() => setConfirmClear(true)} className="px-4 py-2 rounded-lg text-xs font-black uppercase tracking-wider hover:scale-105" style={{ background: 'rgba(255,50,50,0.2)', border: '1px solid rgba(255,50,50,0.4)', color: '#ff4444' }}>Clear All Tiles</button>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-red-300 text-xs font-bold">Are you sure?</span>
                  <button onClick={handleClearAll} disabled={saving} className="px-3 py-1.5 rounded-lg text-xs font-black text-white bg-red-600 hover:bg-red-500 disabled:opacity-60">{saving ? 'Clearing...' : 'Yes, clear all'}</button>
                  <button onClick={() => setConfirmClear(false)} className="px-3 py-1.5 rounded-lg text-xs font-black text-gray-400 hover:text-white" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>Cancel</button>
                </div>
              )}
            </div>
            <div className="p-4 rounded-xl border border-yellow-500/20" style={{ background: 'rgba(255,200,0,0.04)' }}>
              <h3 className="font-black text-yellow-400 text-sm uppercase tracking-wider mb-1 flex items-center gap-2"><FiRefreshCw /> Reset Mock Data</h3>
              <p className="text-gray-400 text-xs mb-3">Reloads the page to restore all original mock tiles.</p>
              <button onClick={() => window.location.reload()} className="px-4 py-2 rounded-lg text-xs font-black uppercase tracking-wider hover:scale-105" style={{ background: 'rgba(255,200,0,0.15)', border: '1px solid rgba(255,200,0,0.3)', color: '#ffcc00' }}>Reload Page</button>
            </div>
            <div className="p-4 rounded-xl border border-pink-500/20" style={{ background: 'rgba(255,0,200,0.04)' }}>
              <h3 className="font-black text-pink-400 text-sm uppercase tracking-wider mb-1 flex items-center gap-2"><FiDownload /> Export All Data</h3>
              <p className="text-gray-400 text-xs mb-3">Download all tile data as a CSV file.</p>
              <button onClick={exportCSV} className="px-4 py-2 rounded-lg text-xs font-black uppercase tracking-wider hover:scale-105" style={{ background: 'rgba(255,0,200,0.15)', border: '1px solid rgba(255,0,200,0.3)', color: '#ff00cc' }}>Export CSV</button>
            </div>
          </div>
        )}
      </div>
    </>
  );

  if (inline) return (
    <div className="min-h-screen flex flex-col" style={{ background: '#080012' }}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-pink-500/20 shrink-0" style={{ background: 'rgba(8,0,18,0.98)', backdropFilter: 'blur(20px)' }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#ff00cc,#cc00ff)' }}><FiShield className="text-white" /></div>
          <div>
            <h2 className="font-black text-white text-lg uppercase tracking-wider" style={{ fontFamily: 'Impact,sans-serif' }}>Admin Panel</h2>
            <p className="text-gray-500 text-xs">1 Million Pixels — Control Center</p>
          </div>
        </div>
        <button onClick={onClose} className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-black uppercase tracking-wider text-gray-400 hover:text-white" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
          <FiArrowLeft className="text-sm" /> Back to Grid
        </button>
      </div>
      <div className="flex flex-col flex-1 max-w-5xl w-full mx-auto">{panelBody}</div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-md p-3">
      <div className="relative w-full max-w-4xl h-[90vh] flex flex-col">
        <div className="absolute -inset-[1.5px] rounded-2xl" style={{ background: 'linear-gradient(135deg,#ff00cc,#cc00ff)' }} />
        <div className="relative rounded-2xl flex flex-col h-full overflow-hidden" style={{ background: '#080012' }}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-pink-500/20 shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#ff00cc,#cc00ff)' }}><FiShield className="text-white" /></div>
              <div>
                <h2 className="font-black text-white text-lg uppercase tracking-wider" style={{ fontFamily: 'Impact,sans-serif' }}>Admin Panel</h2>
                <p className="text-gray-500 text-xs">1 Million Pixels — Control Center</p>
              </div>
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white flex items-center justify-center"><FiX /></button>
          </div>
          {panelBody}
        </div>
      </div>
    </div>
  );
}